/**
 * Build-time feature flags (spec §7/D6, §8.1; issue #56).
 *
 * The AI coach surfaces — Coach AI chat (`CompassCoachAI.jsx`), AI Coach
 * Insights (`AiCoachInsights.jsx`), and the Development plan's "AI
 * Development Plan" tab — carry per-token LLM cost and are off by default
 * until a budget decision is made (spec §7). Off means: absent from nav,
 * their routes redirect rather than render, no disabled buttons or dead
 * ends. The code itself is never deleted — it returns the moment the AI
 * budget decision lands, by flipping this one build-time env var.
 *
 * Set `REACT_APP_ENABLE_AI_COACH=true` at build time (CRA/craco embeds
 * `REACT_APP_*` vars via webpack `DefinePlugin` at build time, same as
 * `REACT_APP_BACKEND_URL` in `lib/api.js`) to turn these surfaces back on.
 * Any other value, or the var being unset entirely, keeps them off — this
 * fails closed, the same default-off polarity `routes/demo.ts`
 * `DEMO_ENABLED` uses on the API side.
 */
export const AI_COACH_ENABLED = process.env.REACT_APP_ENABLE_AI_COACH === "true";

/**
 * One look (D471). The old look is retired: `newUi(screen)` is true for every
 * id in `NEW_UI_SCREENS`. Nothing stored (`localStorage.nw_new_ui`) and nothing
 * built (`REACT_APP_NEW_UI_SCREENS`) is read. The stored key is cleared on
 * load, so an old opt-out cannot linger. `NEW_UI_SCREENS` stays only for
 * newUiScreenContract.test.js until #1687.
 */
try {
  window.localStorage.removeItem("nw_new_ui");
} catch {
  /* blocked storage */
}

/**
 * The screen ids every later wave uses, fixed here once as the contract —
 * a wave that adds a screen adds its id to this one array, and every
 * `newUi(...)` call site elsewhere quotes one of these strings.
 */
export const NEW_UI_SCREENS = [
  "signup",
  "setup",
  "coach-setup",
  "coach-portal-empty",
  "log-ground",
  "log-scorecard",
  "log-saved",
  "record",
  "season-record",
  "plan",
  "plan-milestones",
  // Issue #940, D175 P2 — the `/development` chrome slice (navy TopBar,
  // D92's DestinationRow, the "Your plan" header, the sentence-case tab
  // strip). Depends on `plan` (below, same dependency shape D166 M12 gives
  // `plan-milestones` just above) — an orchestrator ruling, not a
  // technical necessity this file discovers on its own: D175 P2 asks for
  // it explicitly, so `newUi("dev-chrome")` alone (no `plan`) stays
  // exactly today's flag-off behaviour. #876 later chains `reflections` to
  // this same flag.
  "dev-chrome",
  "grounds",
  "ground-card",
  "squad",
  // Issue #973 (D186.3) — the coach's one-player page, `CoachPlayer.dc.html`.
  // Depends on `squad` (the same dependency shape D149/D166 M12/D175 P2 give
  // `season-record`/`plan-milestones`/`dev-chrome` above): a coach reaches
  // this page from a squad row tap, so shipping it without the squad
  // rebuilt would open a redrawn player page from a shipped roster row that
  // never navigates there.
  "coach-player",
  // Issue #874, slice S1 — the front door (`/`) and sign-in (`/login`),
  // redrawn to FrontDoor.dc.html/SignIn.dc.html (design-baseline-round-5).
  // Presentation only: every auth handler (password, passkey, remember-
  // device, the sign-in-link door, session-ended, the demo entry) is reused
  // exactly, via the same AuthContext calls the shipped screens make.
  "signin",
  // Issue #852 (epic #847, voice wave V1) — the Reflections tab rebuilt to
  // ReflectVoice.dc.html/Reflect.dc.html/ReflectMore.dc.html, without a
  // microphone yet (that lands on top of this in #853).
  "reflections",
  // Issue #974 (D186.12) — the interim privacy notice (/privacy) redrawn
  // to Privacy.dc.html. Its own flag, with NO chain: unlike
  // `plan-milestones`/`dev-chrome`/`reflections` above, `newUi("privacy")`
  // is not special-cased below and answers on its raw membership alone.
  "privacy",
  // Issue #975 (D186.8) — Settings, slice A: the page, the account &
  // profile card and coach access, redrawn to Settings.dc.html. Its own
  // flag, with NO chain, the same shape `newUi("privacy")` above has:
  // Settings B (the opened folds and the coach variant) lands behind this
  // SAME flag rather than a chained second one (D186.8 — "one flag for
  // both"), so no special case is added below for it either.
  "settings",
  // Issue #1030 (D107, D130, #726) — the phone match list on `/performance`,
  // redrawn to MatchListFold.dc.html: every card folded to opponent, date,
  // Runs, Runs conceded and Wkts, one tap to open the rest. Its own flag,
  // with NO chain, the same shape `newUi("privacy")`/`newUi("settings")`
  // above have: `newUi("matches")` is not special-cased below and answers
  // on its raw membership alone. Player role only (the page's own router
  // guard also admits admin, who keeps the shipped page).
  "matches",
  // Issue #1316 (Pick XI coach 1 of 3; D297, D302, D309) — Team selection,
  // Your games and Schedule a game (`/coach-portal/team`, `/games`,
  // `/games/schedule`), coach only. Its own flag, with NO chain: the coach's
  // entry lives inside `CoachSquad` (already `squad`) and the player's inside
  // Plan (already `plan`). D202 makes the id ON when nothing is stored, so the
  // flag alone hides nothing. #1318 turns the portal's Team selection
  // on behind it: with the flag on it is a link to /coach-portal/team, with it
  // off the disabled button and its sentence stay (D217).
  "pick-xi",
  // Issue #1514 (D406, D407, D407a) — the guided tour (`/tour`, `/tour/coach`),
  // shown once after player onboarding or coach set-up and replayable from
  // Settings > More. Its own flag, with NO chain: `newUi("tour")` answers on
  // its raw membership alone. With it off, onboarding and coach set-up
  // navigate exactly as before and Settings draws no "Take the tour" row (D217).
  "tour",
  // Issue #1036 (Payments 1; D204.2, D415) — the three pay steps (`/plan`,
  // `/plan/return`, `/plan/failed`), on Stripe test or live keys. Its own flag, with NO
  // chain. The pages have no legacy version, so with it off each route
  // redirects away and renders nothing; the access gate (`BILLING_ENFORCED`)
  // stays off and is a separate decision.
  "pay",
];

export function newUi(screen) {
  return NEW_UI_SCREENS.includes(screen);
}
